import { useState } from "react";
import {
  MoreVertical,
  SlidersHorizontal,
  ChevronDown,
  Trash2,
  FileSpreadsheet,
  FileText,
  Share2,
  Filter,
  CheckCircle2,
  HelpCircle,
  FolderOpen,
  Info,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import {
  Button,
  IconButton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
  ConfirmationDialog,
  type ConfirmationTone,
  type ModalIconVariant,
  Badge,
} from "@/shared/ui";

export function TestUiCoreOverlays() {
  // Dropdown states
  const [showLotCode, setShowLotCode] = useState(true);
  const [showQcStatus, setShowQcStatus] = useState(true);
  const [sortBy, setSortBy] = useState("createdDate");

  // Collapsible state
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Confirmation dialog states
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [dialogTone, setDialogTone] = useState<ConfirmationTone>("destructive");
  const [dialogIconVariant, setDialogIconVariant] = useState<ModalIconVariant>("badge");
  const [lastActionMessage, setLastActionMessage] = useState<string | null>(null);

  const handleOpenDeleteConfirm = () => {
    setDialogTone("destructive");
    setIsConfirmOpen(true);
  };

  const handleOpenApproveConfirm = () => {
    setDialogTone("confirm");
    setIsConfirmOpen(true);
  };

  const handleOpenUnsavedConfirm = () => {
    setDialogTone("unsaved");
    setIsConfirmOpen(true);
  };

  const handleOpenConflictConfirm = () => {
    setDialogTone("conflict");
    setIsConfirmOpen(true);
  };

  const handleExecuteAction = () => {
    setIsConfirmOpen(false);
    const msgMap: Record<ConfirmationTone, string> = {
      destructive: "Đã xóa bản ghi thành công!",
      confirm: "Đã duyệt phiếu thành công!",
      unsaved: "Đã hủy các thay đổi chưa lưu!",
      conflict: "Đã tải lại dữ liệu mới nhất!",
    };
    setLastActionMessage(msgMap[dialogTone]);
    setTimeout(() => setLastActionMessage(null), 3500);
  };

  return (
    <div className="space-y-6">
      {/* Action Flash Message */}
      {lastActionMessage && (
        <div className="rounded-control bg-primary text-on-primary px-4 py-2.5 shadow-md text-caption font-semibold flex items-center gap-2 animate-in fade-in duration-200">
          <CheckCircle2 className="size-4" />
          <span>{lastActionMessage}</span>
        </div>
      )}

      {/* 1. DropdownMenu Showcase */}
      <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-4">
        <div className="flex items-center justify-between border-b border-border/60 pb-2.5">
          <div className="flex items-center gap-2">
            <div className="flex size-7 items-center justify-center rounded-md bg-primary-soft text-primary font-bold">
              <MoreVertical className="size-4" />
            </div>
            <div>
              <h3 className="m-0 text-body font-bold text-text">
                1. DropdownMenu (Menu Thao Tác & Điều Hướng Chuẩn Radix)
              </h3>
              <p className="m-0 text-caption text-text-muted">
                Hỗ trợ phím tắt, menu con (sub-menu), checkbox toggle cột, radio nhóm sắp xếp và semantic styles
              </p>
            </div>
          </div>
          <Badge tone="primary" label="Radix Primitive" />
        </div>

        <div className="flex flex-wrap items-center gap-4">
          {/* Action Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" rightIcon={<ChevronDown className="size-4" />}>
                Thao Tác Dữ Liệu
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel>Thao tác chính</DropdownMenuLabel>
              <DropdownMenuItem onSelect={() => setLastActionMessage("Đã in phiếu")}>
                <FileText className="size-4 mr-2" />
                <span>In Phiếu Yêu Cầu</span>
                <DropdownMenuShortcut>⌘P</DropdownMenuShortcut>
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Share2 className="size-4 mr-2" />
                  <span>Xuất Dữ Liệu</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-48">
                  <DropdownMenuItem onSelect={() => setLastActionMessage("Đã xuất file Excel")}>
                    <FileSpreadsheet className="size-4 mr-2 text-success" />
                    <span>Xuất bảng tính Excel (.xlsx)</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setLastActionMessage("Đã xuất file PDF")}>
                    <FileText className="size-4 mr-2 text-danger" />
                    <span>Xuất tài liệu PDF (.pdf)</span>
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-danger-fg focus:bg-danger-soft focus:text-danger-fg"
                onSelect={handleOpenDeleteConfirm}
              >
                <Trash2 className="size-4 mr-2" />
                <span>Xóa Lệnh Này</span>
                <DropdownMenuShortcut>⌫</DropdownMenuShortcut>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Table Columns Customizer Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" leftIcon={<SlidersHorizontal className="size-4" />}>
                Cột Hiển Thị & Sắp Xếp
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              <DropdownMenuLabel>Cột Bảng Dữ Liệu</DropdownMenuLabel>
              <DropdownMenuCheckboxItem
                checked={showLotCode}
                onCheckedChange={setShowLotCode}
              >
                Mã Lô Nguyên Liệu
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={showQcStatus}
                onCheckedChange={setShowQcStatus}
              >
                Trạng Thái Kiểm Định QC
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Tiêu Chí Sắp Xếp</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={sortBy} onValueChange={setSortBy}>
                <DropdownMenuRadioItem value="createdDate">
                  Ngày Tạo Mới Nhất
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="priority">
                  Theo Độ Ưu Tiên
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="qty">
                  Theo Sản Lượng Tăng Dần
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Quick Icon Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton label="Tùy chọn khác" icon={<MoreVertical className="size-4" />} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onSelect={() => setLastActionMessage("Đã nhân bản lệnh")}>
                Nhân Bản Phiếu
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setLastActionMessage("Đã chuyển phân hệ")}>
                Chuyển Phân Hệ
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* 2. Popover & Collapsible */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Popover Showcase */}
        <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-3">
          <div className="border-b border-border/60 pb-2">
            <h3 className="m-0 text-body font-bold text-text">
              2. Popover (Nội Dung Nổi Linh Hoạt)
            </h3>
            <p className="m-0 text-caption text-text-muted">
              Hiển thị thông tin chi tiết hoặc form cấu hình nhỏ mà không cần chuyển trang
            </p>
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="secondary" leftIcon={<Info className="size-4 text-info" />}>
                Xem Gợi Ý Thông Số Kỹ Thuật
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <HelpCircle className="size-4 text-primary" />
                <span className="text-caption font-bold text-text">Tiêu chuẩn độ ẩm cá tra fillet</span>
              </div>
              <p className="text-caption text-text-secondary m-0 leading-relaxed">
                Hàm lượng nước tối đa theo tiêu chuẩn xuất khẩu EU không vượt quá 85.5%. Tỷ lệ mạ băng chuẩn là 10% ± 1%.
              </p>
              <div className="pt-2 border-t border-border flex justify-end">
                <Badge tone="success" label="Đạt Tiêu Chuẩn HACCP" />
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Collapsible Showcase */}
        <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-3">
          <div className="border-b border-border/60 pb-2">
            <h3 className="m-0 text-body font-bold text-text">
              3. Collapsible (Đóng / Mở Khối Dữ Liệu)
            </h3>
            <p className="m-0 text-caption text-text-muted">
              Thu gọn các bộ lọc nâng cao hoặc trường dữ liệu ít dùng
            </p>
          </div>

          <Collapsible open={isFilterOpen} onOpenChange={setIsFilterOpen}>
            <div className="flex items-center justify-between">
              <span className="text-caption font-semibold text-text">Bộ lọc phụ nâng cao</span>
              <CollapsibleTrigger asChild>
                <Button size="sm" variant="ghost" rightIcon={<ChevronDown className={`size-3.5 transition-transform duration-200 ${isFilterOpen ? "rotate-180" : ""}`} />}>
                  {isFilterOpen ? "Thu gọn" : "Mở rộng"}
                </Button>
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent className="pt-2 space-y-2">
              <div className="rounded-md border border-border bg-bg/50 p-2.5 text-caption space-y-1">
                <div className="flex justify-between">
                  <span className="text-text-muted">Kho đích:</span>
                  <span className="font-semibold text-text">Kho Lạnh Tổng Cần Thơ</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-muted">Mã chứng nhận:</span>
                  <span className="font-mono text-text">ASC-C-01982</span>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </div>

      {/* 4. Accordion & Confirmation Dialog Showcase */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Accordion Showcase */}
        <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-3">
          <div className="border-b border-border/60 pb-2">
            <h3 className="m-0 text-body font-bold text-text">
              4. Accordion (Danh Mục Mở Rộng Đa Tầng)
            </h3>
            <p className="m-0 text-caption text-text-muted">
              Chuẩn Radix Accordion cho tiêu chí đánh giá, checklist chất lượng
            </p>
          </div>

          <Accordion type="single" collapsible defaultValue="qc1" className="w-full">
            <AccordionItem value="qc1">
              <AccordionTrigger>1. Tiêu chuẩn Cảm quan & Ngoại quan</AccordionTrigger>
              <AccordionContent>
                Màu sắc thịt trắng hoặc hồng nhạt đặc trưng, không có vết thâm, không có mùi lạ, cơ thịt săn chắc và có độ đàn hồi tốt.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="qc2">
              <AccordionTrigger>2. Tiêu chuẩn Vi sinh & Kháng sinh</AccordionTrigger>
              <AccordionContent>
                Âm tính hoàn toàn với Salmonella và Vibrio cholerae; dư lượng Chloramphenicol & Enrofloxacin dưới ngưỡng phát hiện LOD.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="qc3">
              <AccordionTrigger>3. Quy cách Đóng gói & Ghi nhãn</AccordionTrigger>
              <AccordionContent>
                Thùng carton 5 lớp có in đầy đủ mã truy xuất nguồn gốc QR Code, chứng nhận ASC/BAP và hạn sử dụng 24 tháng.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* Confirmation Dialog Triggers */}
        <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-3">
          <div className="border-b border-border/60 pb-2 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <h3 className="m-0 text-body font-bold text-text">
                5. Confirmation Dialog (Hộp Thoại Xác Nhận)
              </h3>
              <p className="m-0 text-caption text-text-muted">
                Ngắt luồng thao tác nguy hiểm, phê duyệt, cảnh báo chưa lưu hoặc xung đột dữ liệu
              </p>
            </div>
            {/* Toggle iconVariant */}
            <div className="inline-flex rounded-control bg-bg p-0.5 border border-border shrink-0 self-start sm:self-auto">
              {(["badge", "inline"] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setDialogIconVariant(v)}
                  className={`px-2 py-0.5 text-[11px] rounded font-medium cursor-pointer transition-colors ${
                    dialogIconVariant === v
                      ? "bg-primary text-on-primary font-bold shadow-xs"
                      : "text-text-muted hover:text-text"
                  }`}
                >
                  {v === "badge" ? "Badge Icon" : "Inline Icon"}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
            <div className="flex items-center justify-between rounded-md border border-danger/30 bg-danger-soft/20 p-2.5">
              <div>
                <p className="m-0 text-caption font-bold text-danger-fg">1. Xóa Lệnh Sản Xuất</p>
                <p className="m-0 text-[11px] text-text-muted">Tone destructive · Icon Trash2 đỏ</p>
              </div>
              <Button size="sm" variant="danger" onClick={handleOpenDeleteConfirm}>
                Xác Nhận Xóa
              </Button>
            </div>

            <div className="flex items-center justify-between rounded-md border border-primary/30 bg-primary-soft/20 p-2.5">
              <div>
                <p className="m-0 text-caption font-bold text-primary-fg">2. Phê Duyệt Nghiệp Vụ</p>
                <p className="m-0 text-[11px] text-text-muted">Tone confirm · Icon CheckCircle xanh</p>
              </div>
              <Button size="sm" variant="primary" onClick={handleOpenApproveConfirm}>
                Xác Nhận Duyệt
              </Button>
            </div>

            <div className="flex items-center justify-between rounded-md border border-warning/30 bg-warning-soft/20 p-2.5">
              <div>
                <p className="m-0 text-caption font-bold text-warning-fg">3. Hủy Thay Đổi / Rời Trang</p>
                <p className="m-0 text-[11px] text-text-muted">Tone unsaved · Icon AlertTriangle vàng</p>
              </div>
              <Button size="sm" variant="secondary" onClick={handleOpenUnsavedConfirm}>
                Hủy Thay Đổi
              </Button>
            </div>

            <div className="flex items-center justify-between rounded-md border border-info/30 bg-info-soft/20 p-2.5">
              <div>
                <p className="m-0 text-caption font-bold text-info-fg">4. Xung Đột Dữ Liệu</p>
                <p className="m-0 text-[11px] text-text-muted">Tone conflict · Icon RefreshCw lam</p>
              </div>
              <Button size="sm" variant="secondary" onClick={handleOpenConflictConfirm}>
                Tải Lại Trang
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Actual ConfirmationDialog instance */}
      <ConfirmationDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        iconVariant={dialogIconVariant}
        tone={dialogTone}
        title={
          dialogTone === "destructive"
            ? "Xác Nhận Xóa Lệnh MO-2026-0318?"
            : dialogTone === "confirm"
              ? "Phê Duyệt Lệnh Sản Xuất MO-2026-0318?"
              : dialogTone === "unsaved"
                ? "Hủy Thay Đổi Phiếu Yêu Cầu?"
                : "Xung Đột Phiên Bản Dữ Liệu!"
        }
        description={
          dialogTone === "destructive"
            ? "Thao tác này sẽ hủy lệnh sản xuất và giải phóng toàn bộ nguyên liệu đã khoanh giữ trong kho. Hành động này không thể hoàn tác."
            : dialogTone === "confirm"
              ? "Lệnh sẽ được chuyển sang trạng thái Sẵn Sàng Chế Biến và thông báo cho Quản đốc phân xưởng STVQ."
              : dialogTone === "unsaved"
                ? "Các thông tin bạn vừa chỉnh sửa chưa được lưu vào hệ thống. Bạn có chắc chắn muốn thoát?"
                : "Dữ liệu bản ghi này vừa được người dùng khác cập nhật. Vui lòng tải lại để thấy thông tin mới nhất."
        }
        confirmLabel={
          dialogTone === "destructive"
            ? "Xóa Lệnh Vĩnh Viễn"
            : dialogTone === "confirm"
              ? "Xác Nhận Phê Duyệt"
              : dialogTone === "unsaved"
                ? "Hủy Thay Đổi"
                : "Tải Lại Dữ Liệu"
        }
        onConfirm={handleExecuteAction}
      />
    </div>
  );
}
